
import {
  ShieldCheck,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  BarChart3,
  ShieldAlert,
  CheckCircle2,
  Layers,
  DollarSign,
  Percent,
  Activity,
  Table as TableIcon,
  Shield
} from 'lucide-react';
import WrennenLogoMark from './WrennenLogoMark';
import { motion } from 'motion/react';
import SummaryCard from './SummaryCard';

interface LandingPageProps {
  onStart: () => void;
  onLogin: () => void;
  onSignUp: () => void;
}

const LandingPage = (props: LandingPageProps) => {
  const { onStart, onLogin, onSignUp } = props;
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-emerald-900 selection:text-emerald-100 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2.5 group cursor-pointer">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-900 group-hover:scale-110 transition-transform">
              <WrennenLogoMark size={22} className="text-white" />
            </div>
            <span className="text-xl font-black tracking-[0.18em] text-white uppercase">WRENNEN</span>
          </div>
          <div className="hidden md:flex items-center gap-10 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            <a href="#portfolio" className="hover:text-emerald-500 transition-colors">Portfolio</a>
            <a href="#risk" className="hover:text-emerald-500 transition-colors">Risk Engine</a>
            <a href="#analysis" className="hover:text-emerald-500 transition-colors">Analysis</a>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={onLogin}
              className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-500 transition-colors px-4"
            >
              Login
            </button>
            <button
              onClick={onSignUp}
              className="bg-emerald-600 text-white px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 shadow-lg shadow-emerald-900 transition-all active:scale-95"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-emerald-900/20 rounded-full blur-[120px] opacity-40"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-slate-800/30 rounded-full blur-[120px] opacity-40"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-950 border border-emerald-800 text-emerald-400 text-[10px] font-black uppercase tracking-[0.2em] mb-10">
              <Activity size={12} className="text-emerald-500" />
              Institutional Real Estate Intelligence
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-10 leading-[0.9] max-w-5xl mx-auto">
              Know the Risk of Every Property You <br />
              <span className="text-emerald-500">Own.</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-14 leading-relaxed font-medium">
              Underwrite assets, monitor portfolio exposure, and stress-test real estate risk with an institutional-grade operating system.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-24">
              <button
                onClick={onSignUp}
                className="w-full sm:w-auto px-10 py-5 bg-emerald-600 text-white rounded-[2rem] font-black uppercase tracking-widest text-xs hover:bg-emerald-700 shadow-2xl shadow-emerald-900/50 flex items-center justify-center gap-3 group transition-all"
              >
                Start Your Portfolio <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={onLogin}
                className="w-full sm:w-auto px-10 py-5 bg-slate-800 border border-slate-700 text-white rounded-[2rem] font-black uppercase tracking-widest text-xs hover:bg-slate-700 transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                Login To Continue
              </button>
            </div>

            {/* Real Product UI: Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left max-w-6xl mx-auto">
              <SummaryCard label="Portfolio Value" value="$4.2M" subtext="Combined Purchase" icon={<DollarSign size={20} />} />
              <SummaryCard label="Weighted IRR" value="18.4%" subtext="Equity Velocity" icon={<TrendingUp size={20} />} />
              <SummaryCard label="Portfolio MOIC" value="2.4x" subtext="Capital Multiple" icon={<ChevronRight size={20} />} />
              <SummaryCard label="Year 1 CoC" value="7.2%" subtext="Blended Yield" icon={<Percent size={20} />} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-32 bg-slate-900 border-y border-slate-800 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="lg:w-1/2">
              <h2 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] mb-6">The Blind Spot</h2>
              <h3 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-tight mb-8">Most Investors Only See <br /><span className="text-rose-500">Half the Picture.</span></h3>
              <p className="text-lg text-slate-300 mb-12 font-medium leading-relaxed">
                Standard calculators assume best-case scenarios. Wrennen shows what happens when conditions turn, capital needs surface, and liquidity gets tight.
              </p>
              <div className="space-y-6">
                {[
                  { icon: <AlertTriangle className="text-rose-500" size={20} />, title: "Unexpected Repairs", desc: "One major failure (HVAC, Roof) can destroy 3 years of returns." },
                  { icon: <ShieldAlert className="text-amber-500" size={20} />, title: "Liquidity Crushes", desc: "Investors buy without simulating the crunch of a worst-case month." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-5 p-6 rounded-3xl border border-slate-800 bg-slate-800/30">
                    <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center shadow-sm shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-white uppercase tracking-widest mb-1">{item.title}</h4>
                      <p className="text-xs text-slate-400 font-medium">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              {/* Real Product UI: Risk Impact Table */}
              <div className="bg-slate-800 rounded-[3rem] p-10 shadow-2xl shadow-black/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-10 opacity-5">
                  <ShieldAlert size={160} className="text-white" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-10">
                    <h3 className="text-white font-black uppercase tracking-[0.2em] text-[10px]">Risk Simulation: HVAC Failure</h3>
                    <div className="px-3 py-1 bg-rose-500/20 border border-rose-500/30 rounded-full">
                      <span className="text-rose-400 text-[9px] font-black uppercase tracking-widest">High Impact</span>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="flex items-center justify-between border-b border-slate-700 pb-5">
                      <span className="text-slate-400 text-sm font-medium">Projected Cash Flow</span>
                      <span className="text-emerald-400 font-black text-xl tracking-tighter">+$1,240 /mo</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-700 pb-5">
                      <span className="text-slate-400 text-sm font-medium">Simulated Repair Cost</span>
                      <span className="text-rose-400 font-black text-xl tracking-tighter">-$6,500</span>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-white text-base font-black uppercase tracking-widest">Adjusted Year 1 ROI</span>
                      <div className="text-right">
                        <div className="text-rose-400 text-4xl font-black tracking-tighter">4.2%</div>
                        <div className="text-slate-500 text-[10px] font-bold line-through tracking-widest uppercase">12.8% ORIGINAL</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Dashboard Section */}
      <section id="portfolio" className="py-32 px-6 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] mb-6">Portfolio Intelligence</h2>
            <h3 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-6">
              Your Entire Portfolio. <br />
              <span className="text-emerald-500">One Unified View.</span>
            </h3>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto font-medium">
              Stop switching between spreadsheets. Monitor aggregate cash flow, average ROI, and total equity across your entire holdings in real-time.
            </p>
          </div>

          {/* Real Product UI: Portfolio Member List */}
          <div className="bg-slate-800 rounded-[3rem] border border-slate-700/50 shadow-xl shadow-black/30 overflow-hidden">
            <div className="p-8 border-b border-slate-700 bg-slate-800/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-900">
                  <Layers className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white tracking-tight">Strategic Growth Portfolio</h3>
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">6 Active Assets • $4.2M AUM</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 shadow-sm hover:text-emerald-500 transition-colors">
                  <BarChart3 size={14} className="inline mr-2 text-slate-500" /> Dashboard
                </div>
                <div className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-900">
                  <TableIcon size={14} className="inline mr-2" /> Spreadsheet
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400 font-black uppercase tracking-[0.2em]">
                    <th className="px-8 py-6">Property</th>
                    <th className="px-8 py-6 text-right">Value</th>
                    <th className="px-8 py-6 text-right">IRR</th>
                    <th className="px-8 py-6 text-right">MOIC</th>
                    <th className="px-8 py-6 text-right">Risk Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {[
                    { name: "The Heights Apartments", value: "$1,250,000", irr: "18.4%", moic: "2.4x", risk: "Low", riskColor: "text-emerald-500" },
                    { name: "Oak Ridge Quadplex", value: "$840,000", irr: "14.2%", moic: "1.9x", risk: "Medium", riskColor: "text-amber-500" },
                    { name: "Sunset Portfolio (3 Units)", value: "$2,100,000", irr: "21.8%", moic: "3.1x", risk: "Low", riskColor: "text-emerald-500" }
                  ].map((p, i) => (
                    <tr key={i} className="group hover:bg-slate-700/30 transition-colors">
                      <td className="px-8 py-6 font-black text-white text-sm">{p.name}</td>
                      <td className="px-8 py-6 text-right text-slate-400 font-mono">{p.value}</td>
                      <td className="px-8 py-6 text-right text-emerald-400 font-black">{p.irr}</td>
                      <td className="px-8 py-6 text-right text-cyan-400 font-black">{p.moic}</td>
                      <td className={`px-8 py-6 text-right font-black uppercase tracking-widest text-[10px] ${p.riskColor}`}>{p.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 bg-slate-800/30 border-t border-slate-700 text-center">
              <button onClick={onLogin} className="text-emerald-500 font-black uppercase tracking-widest text-[10px] hover:text-emerald-400 transition-colors">
                View Full Portfolio Analysis <ChevronRight size={14} className="inline ml-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Simulation Section */}
      <section id="risk" className="py-32 px-6 bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative bg-slate-800 rounded-[3rem] border border-slate-700/50 p-10 shadow-2xl shadow-black/40">
                <div className="flex items-center justify-between mb-10">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Risk Workflow</h4>
                  <div className="px-4 py-1.5 bg-rose-500/20 text-rose-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-rose-500/30">
                    In Product
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="p-6 bg-slate-900 rounded-2xl border border-slate-700 shadow-sm">
                    <p className="text-sm font-black text-white uppercase tracking-widest">Saved Property Risk Dashboard</p>
                    <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                      The real stress testing lives inside each property&apos;s risk dashboard, where one-time expenses and condition updates can rerun the actual model.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-5 bg-slate-900 rounded-2xl border border-slate-700">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">What You Can Add</p>
                      <p className="text-sm font-black text-white mt-3">Roof, HVAC, plumbing, make-ready, and other one-time expenses.</p>
                    </div>
                    <div className="p-5 bg-slate-900 rounded-2xl border border-slate-700">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">What Recalculates</p>
                      <p className="text-sm font-black text-white mt-3">Cash flow, reserves, exit returns, and property risk score.</p>
                    </div>
                    <div className="p-5 bg-slate-900 rounded-2xl border border-slate-700">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Why It Matters</p>
                      <p className="text-sm font-black text-white mt-3">You can test the hit before it happens instead of trusting static assumptions.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] mb-6">The Core Hook</h2>
              <h3 className="text-4xl md:text-7xl font-black text-white tracking-tight mb-10 leading-[1.1]">What Happens When <span className="text-emerald-500">Something Goes Wrong?</span></h3>
              <p className="text-lg text-slate-300 mb-12 font-medium leading-relaxed">
                Most calculators assume zero surprises. Wrennen lets you model the inevitable. See exactly how a major repair or vacancy impacts yield, liquidity, and exit returns before you commit capital.
              </p>
              <ul className="space-y-6">
                {[
                  "Model system failures (Roof, HVAC, Plumbing)",
                  "Stress-test vacancy durations",
                  "Calculate recovery timelines for major CapEx",
                  "Plan cash reserves based on asset age"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-4 text-slate-300 font-bold text-sm uppercase tracking-widest">
                    <CheckCircle2 size={20} className="text-emerald-500" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="analysis" className="py-32 px-6 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] mb-6">Capabilities</h2>
            <h3 className="text-4xl md:text-5xl font-black text-white tracking-tight">Everything You Need to Invest with Precision</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <BarChart3 size={24} />, title: "Property Analysis", desc: "Analyze deals before you buy with institutional-grade pro-formas." },
              { icon: <Layers size={24} />, title: "Portfolio Tracking", desc: "See all your investments in one place with aggregated metrics." },
              { icon: <ShieldAlert size={24} />, title: "Risk Simulation", desc: "Model real-world scenarios instantly to see their financial impact." },
              { icon: <TrendingUp size={24} />, title: "Reserve Planning", desc: "Know exactly how much cash buffer you need for every asset." }
            ].map((feature, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700/50 p-10 rounded-[2.5rem] hover:shadow-xl hover:shadow-black/30 hover:border-emerald-500/30 transition-all group">
                <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-900 text-white">
                  {feature.icon}
                </div>
                <h4 className="text-sm font-black text-white uppercase tracking-widest mb-4">{feature.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed font-medium">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl shadow-emerald-900/50">
          <div className="absolute top-0 right-0 p-12 opacity-5">
            <ShieldCheck size={240} className="text-white" />
          </div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-8">
              Stop Guessing. <br />
              Start Knowing Your Numbers.
            </h2>
            <p className="text-xl text-emerald-50 mb-12 max-w-2xl mx-auto font-medium">
              Join investors and operators who use Wrennen to protect capital, price risk correctly, and manage portfolio performance with confidence.
            </p>
            <button
              onClick={onSignUp}
              className="bg-white text-emerald-600 px-12 py-6 rounded-[2rem] font-black uppercase tracking-widest text-sm hover:bg-emerald-50 shadow-xl transition-all hover:-translate-y-1 active:scale-95"
            >
              Start Your Portfolio Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 border-t border-slate-800 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between gap-12 mb-20">
            <div className="max-w-xs">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <WrennenLogoMark size={18} className="text-white" />
                </div>
                <span className="text-lg font-black tracking-[0.18em] text-white uppercase">WRENNEN</span>
              </div>
              <p className="text-sm text-slate-400 font-medium leading-relaxed">
                The financial command center for real estate investors.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-12">
              <div>
                <h5 className="text-[10px] font-black text-white uppercase tracking-[0.2em] mb-6">Product</h5>
                <ul className="space-y-4">
                  {['Analysis', 'Portfolio', 'Risk Engine', 'Pricing'].map(item => (
                    <li key={item}><a href="#" className="text-xs text-slate-400 hover:text-emerald-500 font-medium transition-colors">{item}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 className="text-[10px] font-black text-white uppercase tracking-[0.2em] mb-6">Company</h5>
                <ul className="space-y-4">
                  {['About', 'Blog', 'Careers', 'Contact'].map(item => (
                    <li key={item}><a href="#" className="text-xs text-slate-400 hover:text-emerald-500 font-medium transition-colors">{item}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 className="text-[10px] font-black text-white uppercase tracking-[0.2em] mb-6">Legal</h5>
                <ul className="space-y-4">
                  {['Privacy', 'Terms', 'Security'].map(item => (
                    <li key={item}><a href="#" className="text-xs text-slate-400 hover:text-emerald-500 font-medium transition-colors">{item}</a></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-10 border-t border-slate-800">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              © 2026 Wrennen Technologies Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <Shield size={14} className="text-emerald-500" /> Secure Financial Intelligence
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
